import React, { useState } from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Divider,
  Avatar,
  Grid,
  TextField,
  Chip,
  Alert,
  Snackbar
} from '@mui/material';
import { 
  Person as PersonIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Logout as LogoutIcon,
  CreditCard as CreditCardIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { updateProfile } from 'firebase/auth';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../services/firebase';

const ProfilePage: React.FC = () => {
  // Auth context
  const { currentUser, userData, signOut } = useAuth();
  
  // Navigation
  const navigate = useNavigate();
  
  // State for editing
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [name, setName] = useState<string>(currentUser?.displayName || '');
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });
  
  // Get subscription name
  const getSubscriptionName = (): string => {
    switch (userData?.subscription) {
      case 'premium':
        return 'Premium';
      case 'basic':
        return 'Basic';
      default:
        return 'Free';
    }
  };
  
  // Get subscription color
  const getSubscriptionColor = (): 'primary' | 'secondary' | 'default' => {
    switch (userData?.subscription) {
      case 'premium':
        return 'secondary';
      case 'basic':
        return 'primary';
      default:
        return 'default';
    }
  };
  
  // Handle edit toggle
  const handleEditToggle = () => {
    setIsEditing(!isEditing);
    if (!isEditing) {
      setName(currentUser?.displayName || '');
    }
  };
  
  // Handle save profile
  const handleSaveProfile = async () => {
    try {
      if (currentUser) {
        // Update display name in Firebase Auth
        await updateProfile(currentUser, {
          displayName: name
        });
        
        // Update user data in Firestore
        const userRef = doc(db, 'users', currentUser.uid);
        await updateDoc(userRef, {
          displayName: name,
          updatedAt: serverTimestamp()
        });
        
        setIsEditing(false);
        setNotification({
          open: true,
          message: 'Profile updated successfully',
          severity: 'success'
        });
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setNotification({
        open: true,
        message: 'Error updating profile',
        severity: 'error'
      });
    }
  };
  
  // Handle sign out
  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (err) {
      console.error('Error signing out:', err);
      setNotification({
        open: true,
        message: 'Error signing out',
        severity: 'error'
      });
    }
  };
  
  // Handle notification close
  const handleNotificationClose = () => {
    setNotification({
      ...notification,
      open: false
    });
  };
  
  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Paper 
        elevation={0} 
        className="glass-container"
        sx={{ 
          p: 4,
          mb: 4
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <PersonIcon sx={{ mr: 1 }} />
          <Typography variant="h5" component="h1">
            My Profile
          </Typography>
        </Box>
        
        <Grid container spacing={4}>
          {/* Profile Info */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Avatar
                sx={{ 
                  width: 120, 
                  height: 120, 
                  mb: 2,
                  bgcolor: 'primary.main',
                  fontSize: '3rem'
                }}
              >
                {(currentUser?.displayName || currentUser?.email || 'U').charAt(0).toUpperCase()}
              </Avatar>
              
              <Chip
                label={`${getSubscriptionName()} Plan`}
                color={getSubscriptionColor()}
                sx={{ mb: 2 }}
              />
              
              <Button
                component={Link}
                to="/pricing"
                variant="outlined"
                color="primary"
                startIcon={<CreditCardIcon />}
                sx={{ mb: 2, borderRadius: 4 }}
              >
                Upgrade Plan
              </Button>
              
              <Button
                variant="outlined"
                color="error"
                startIcon={<LogoutIcon />}
                onClick={handleSignOut}
                sx={{ borderRadius: 4 }}
              >
                Sign Out
              </Button>
            </Box>
          </Grid>
          
          {/* Profile Details */}
          <Grid item xs={12} md={8}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Account Details
                </Typography>
                
                <Button
                  variant="text"
                  color={isEditing ? 'error' : 'primary'}
                  startIcon={isEditing ? <CancelIcon /> : <EditIcon />}
                  onClick={handleEditToggle}
                >
                  {isEditing ? 'Cancel' : 'Edit'}
                </Button>
              </Box>
              
              {isEditing ? (
                <Box component="form" sx={{ mb: 3 }}>
                  <TextField
                    fullWidth
                    label="Name"
                    variant="outlined"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  
                  <TextField
                    fullWidth
                    label="Email"
                    variant="outlined"
                    value={currentUser?.email || ''}
                    disabled
                    sx={{ mb: 3 }}
                  />
                  
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveProfile}
                    sx={{ borderRadius: 4 }}
                  >
                    Save Changes
                  </Button>
                </Box>
              ) : (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Name:</strong> {currentUser?.displayName || 'Not set'}
                  </Typography>
                  
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Email:</strong> {currentUser?.email}
                  </Typography>
                  
                  <Typography variant="body1">
                    <strong>Member Since:</strong> {userData?.createdAt ? new Date(userData.createdAt).toLocaleDateString() : 'Unknown'}
                  </Typography>
                </Box>
              )}
              
              <Divider sx={{ my: 3 }} />
              
              <Box>
                <Typography variant="h6" gutterBottom>
                  Subscription Details
                </Typography>
                
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Current Plan:</strong> {getSubscriptionName()}
                </Typography>
                
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Billing Cycle:</strong> {userData?.billingCycle || 'Monthly'}
                </Typography>
                
                <Typography variant="body1">
                  <strong>Next Billing Date:</strong> {userData?.nextBillingDate ? new Date(userData.nextBillingDate).toLocaleDateString() : 'N/A'}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Usage Stats */}
      <Paper 
        elevation={0} 
        className="glass-container"
        sx={{ 
          p: 4
        }}
      >
        <Typography variant="h6" gutterBottom>
          Usage Statistics
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <Typography variant="h3" color="primary">
                {userData?.capsuleCount || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Capsules Created
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <Typography variant="h3" color="primary">
                {userData?.totalTimeSaved || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Minutes Saved
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <Typography variant="h3" color="primary">
                {userData?.remainingCredits || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Remaining Credits
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleNotificationClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleNotificationClose} 
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ProfilePage;
